import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Box, Button, TextField, Typography, Paper, CircularProgress, Skeleton } from '@mui/material';
import { LinkOffOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useVerify from 'app/hooks/useVerify';
import ItemVerifier from './ItemVerifier';

const PageVerifier = () => {
  const { ConnectionSecure, ValidateMailCode } = useVerify();
  const [searchParams] = useSearchParams();
  const t = searchParams.get("t");

  const [loading, setLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);

  const [values, setValues] = useState(Array(6).fill(""));
  const inputsRef = useRef([]);
  const otpBoxRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [project, setProject] = useState(null);

  useEffect(() => {
    const checkSecure = async () => {
      try {
        setLoading(true);
        const res = await ConnectionSecure(t);
        setIsValid(!!res?.success);
      } catch (err) {
        setIsValid(false);
      } finally {
        setLoading(false);
      }
    };

    checkSecure();
  }, [t]);


  const handleChange = (e, index) => {
    const val = e.target.value.replace(/\D/g, "");
    if (val.length > 1) return;

    const newValues = [...values];
    newValues[index] = val;
    setValues(newValues);

    if (val && index < 5) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !values[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handleSubmit = async () => {
    const code = values.join("");
    if (code.length !== 6) return;

    setSubmitting(true);
    try {
      const res = await ValidateMailCode(t, code);
      if (res?.success) {
        setProject(res.payload);
      } else {
        toast.error(res?.message || "Invalid security code !", { position : "bottom-center"})
      }
    } catch (err) {
      toast.error("Internal server error, please contact your administrator !", { position : "bottom-center"})
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          background: "linear-gradient(135deg, #ee7752, #e73c7e, #23a6d5, #23d5ab)",
          backgroundSize: "400% 400%",
          animation: "gradientAnimation 15s ease infinite",
          color: "#fff",
          textAlign: "center",
          gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#fff" }}>
          VERIFICATION LINK
        </Typography>
        <CircularProgress />
        <Typography variant="body2" sx={{ color: "#fff" }}>
          Please wait a moment while we verify the link...
        </Typography>
      </Box>
    );
  }

  if (!isValid) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(135deg, #ee7752, #e73c7e, #23a6d5, #23d5ab)",
          backgroundSize: "400% 400%",
          animation: "gradientAnimation 15s ease infinite",
        }}
      >
        <Paper sx={{ p: 3, borderRadius: 2, textAlign: "center", opacity: '0.85' }}>
          <Box sx={{ mb: 4, mt: 4 }}>
            <img
              src="/assets/images/logo-dashboard.png"
              alt="Logo"
              style={{ width: 220, height: "auto" }}
            />
          </Box>
          <LinkOffOutlined style={{ fontSize: 40, color: "#c62828" }} />
          <br />
          <Typography variant="body2" color="text.secondary">
            This link has expired<br /> To continue the approval process, please click the button below
          </Typography>
          <br />
          <Button
            sx={{
              mt: 2,
              mb: 2,
              color: "white",
              "&:hover": {
                color: "white",
                backgroundColor: "#c62828",
              },
            }}
            component={Link} to="/session/signin" variant="contained" color="error"
          >
            GO TO LOGIN PAGE
          </Button>
        </Paper>
      </Box>
    );
  }

  if (project !== null) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #ee7752, #e73c7e, #23a6d5, #23d5ab)",
          backgroundSize: "400% 400%",
          animation: "gradientAnimation 15s ease infinite",
          overflow: "hidden",
          width: "100%"
        }}
      >
        {project ? (
          <ItemVerifier project={project} />
        ) : (
          <Box sx={{ width: "80%", maxWidth: 900 }}>
            <Skeleton variant="text" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={400} />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #ee7752, #e73c7e, #23a6d5, #23d5ab)",
        backgroundSize: "400% 400%",
        animation: "gradientAnimation 15s ease infinite",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 2,
          width: 420,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 3,
          opacity: '0.85'
        }}
      >
        <Box sx={{ mb: 4, mt: 4 }}>
          <img
            src="/assets/images/logo-dashboard.png"
            alt="Logo"
            style={{ width: 180, height: "auto" }}
          />
        </Box>

        <Typography
          variant="h5"
          fontWeight="bold"
          gutterBottom
          sx={{
            background: "linear-gradient(90deg, black, red)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          VERIFICATION CODE
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: "center" }}
        >
          Please enter the 6-digit code sent to your email.
        </Typography>

        <Box ref={otpBoxRef} sx={{ display: "flex", gap: 1.5, mb: 3 }}>
          {values.map((val, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputsRef.current[index] = el)}
              autoComplete='off'
              value={val}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              inputProps={{
                maxLength: 1,
                style: { textAlign: "center", fontSize: "1.5rem" },
              }}
              sx={{ width: 48 }}
            />
          ))}
        </Box>

        <Box sx={{ width: '100%', p: 2 }}>
          <Button
            variant="contained"
            color="error"
            fullWidth
            sx={{ textTransform: "capitalize", mb: 2 }}
            size="large"
            onClick={handleSubmit}
            disabled={values.join("").length !== 6 || submitting}
          >
            {submitting ? <CircularProgress size={24} sx={{ color: "white" }} /> : "VERIFY CODE"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default PageVerifier;
